// Family workspace: the single-family detail screen that replaces the old
// VariantsModal. Mounted at `${inquiry-processor match}/master-products/:familyId`
// — registered ahead of the list route in index.tsx so the non-exact list
// path doesn't swallow it.

import { Box, Center, Spinner } from '@elkaso-app/web-design';
import { useGetMasterProductFamilyApi } from 'apis/master-product-families';
import { useGetMasterProductsApi } from 'apis/master-products';
import { FamilyActivityPanel } from 'pages/inquiry-processor/components/family-workspace/family-activity-panel';
import { FamilyHeader } from 'pages/inquiry-processor/components/family-workspace/family-header';
import { FamilySuppliersPanel } from 'pages/inquiry-processor/components/family-workspace/family-suppliers-panel';
import { variantLabel } from 'pages/inquiry-processor/components/family-workspace/packaging';
import { EditableVariantsGrid } from 'pages/inquiry-processor/components/family-workspace/variants-grid/variants-grid';
import { useEffect, useMemo, useState } from 'react';
import { Redirect, useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';
import { changeTitle } from 'redux/actions';
import { useAppDispatch } from 'redux/hooks';

// Navigation state the merge modal hands off on its post-merge redirect (see
// merge-family-modal.tsx) — consumed exactly once below.
interface MergeHighlightNavState {
  mergedVariantIds?: number[];
  mergeSourceName?: string;
}

interface Highlight {
  ids: number[];
  sourceName: string;
}

export const FamilyWorkspaceScreen = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation<MergeHighlightNavState | undefined>();
  const match = useRouteMatch();
  const { familyId } = useParams<{ familyId: string }>();
  // Parse once: a malformed/non-numeric deep link (e.g. `/master-products/NaN`)
  // must never reach the hooks below as `NaN` — `NaN != null` is true, so it
  // would slip past the family hook's guard and fire a bogus request before
  // the 404 redirect below ever runs.
  const parsedId = Number(familyId);
  const id = Number.isFinite(parsedId) ? parsedId : undefined;

  const [highlight, setHighlight] = useState<Highlight | null>(null);

  // Consume the post-merge navigation state exactly once per family route:
  // stash it into local state, then immediately `history.replace` the same
  // path with no state so a refresh or back-navigation never re-highlights.
  // Keyed on `familyId` (not on mount alone) so that navigating from one
  // family workspace straight into another — which this screen doesn't
  // remount for, only its keyed grid/activity panel do — both consumes a
  // freshly-arriving highlight and clears a stale one when there isn't one.
  useEffect(() => {
    const state = location.state;
    if (state?.mergedVariantIds?.length) {
      setHighlight({ ids: state.mergedVariantIds, sourceName: state.mergeSourceName ?? '' });
      history.replace(location.pathname);
    } else {
      setHighlight(null);
    }
    // Intentionally keyed on `familyId` only — see comment above and the
    // no-react-hooks-eslint-plugin note in variants-grid.tsx.
  }, [familyId]);

  const { data: family, isLoading, isError } = useGetMasterProductFamilyApi(id);
  const { data: variantsData } = useGetMasterProductsApi(id, { enabled: !!family });
  const variants = variantsData?.data ?? [];

  // "brand · spec · packagingSummary" label per variant id, for the suppliers
  // panel's coverage tooltip — built once here so the panel doesn't need its
  // own copy of the grid's variants.
  const variantLabelById = useMemo(() => {
    const map = new Map<number, string>();
    variants.forEach((variant) => map.set(variant.id, variantLabel(variant)));
    return map;
  }, [variants]);

  useEffect(() => {
    dispatch(changeTitle('Master Products'));
  }, [dispatch]);

  if (isLoading) {
    return (
      <Center py='24'>
        <Spinner color='red.500' />
      </Center>
    );
  }

  // Unknown/inaccessible family — bail to 404 once loading has settled (never
  // mid-load, or a fresh page load would flash the 404 before the fetch resolves).
  if (isError || !family) {
    return <Redirect to='/404' />;
  }

  // `match.url` is the resolved `.../master-products/:familyId` URL — strip
  // the id segment to get back to the list, preserving whatever prefix this
  // module is mounted under.
  const listUrl = match.url.slice(0, match.url.lastIndexOf('/'));

  return (
    <Box p={{ base: '2', md: '4' }}>
      <FamilyHeader family={family} variantCount={variants.length} onBack={() => history.push(listUrl)} />

      <Box mt='4'>
        {/* Variants grid — always-on editor, no separate "read" mode to
            return to (the workspace IS the editor). Within a single family it
            owns its own row state and re-seeds itself from `variants` via an
            explicit `reset`, gated on its own dirty flag, whenever fresh data
            arrives (see variants-grid.tsx) — so a background refetch never
            silently discards in-progress edits, and a post-save refetch still
            repopulates server-assigned ids to prevent duplicate re-POSTs.
            It's still keyed on family.id below so navigating source→target
            after a merge always remounts with fresh state, even when the
            target family's query is already warm in the cache. */}
        <EditableVariantsGrid
          key={family.id}
          familyId={family.id}
          familyCategoryId={family.catalogCategoryId}
          variants={variants}
          highlightedIds={highlight?.ids}
          highlightSourceName={highlight?.sourceName}
          onClearHighlight={() => setHighlight(null)}
        />
      </Box>

      <Box mt='4'>
        <FamilySuppliersPanel familyId={family.id} variantLabelById={variantLabelById} variants={variants} />
      </Box>

      <Box mt='4'>
        {/* Remount per family keeps React state and the uncontrolled Accordion in
            sync — same rationale as the grid's key above. */}
        <FamilyActivityPanel key={family.id} familyId={family.id} />
      </Box>
    </Box>
  );
};
